<template>
    <!-- Top performers (gamification) - a modal, not an always-visible card: an inline card would eat
         space on every visit whether or not anyone was looking at it. Same Makeable Modal pattern as
         ManageObjectivesModal.vue alongside it in the toolbar. Gating (>= 3 Farms, at least one
         qualifying entry) is the caller's call, not this component's - Index.vue always renders
         <TopPerformersModal>, passing `disabledReason` once either condition fails. Disabled rather
         than hidden - a control that quietly disappears reads as broken, not as "not applicable yet" -
         so the button stays put and its tooltip explains which condition is unmet. -->
    <Modal close-esc-event="keydown" :close-button="false" modal-class="w-full max-w-md bg-white m-auto rounded-xl border-0" mask-class="grid">
        <template #toggle="scope">
            <button
                type="button"
                class="grid h-9 w-9 shrink-0 place-content-center rounded-lg border transition-colors"
                :class="disabledReason ? 'cursor-not-allowed border-gray-200 text-gray-300' : 'border-amber-300 text-amber-600 hover:bg-amber-50'"
                :disabled="!!disabledReason"
                :title="disabledTooltip"
                :aria-label="disabledTooltip"
                @click="scope.open"
            >
                <Medal class="h-4 w-4" aria-hidden="true" />
            </button>
        </template>

        <template #header="scope">
            <CloseModalButton @close="scope.close"></CloseModalButton>
        </template>

        <!-- Header is its own bordered block, not just the title floating in the same padding as the
             list: CloseModalButton is absolutely positioned, so it only ever looks right once the
             content below reserves its own real header space, the way ManageObjectivesModal
             alongside this one already does - otherwise the title crowds the top-right close button.

             Best/Worst split: mutually exclusive - Worst is drawn from whatever's left once Best has
             taken its slice (see leaderboard.ts), so a Barn never shows in both. Stacked, not
             side-by-side, at the modal's existing width. Worst is deliberately understated next to
             Best - plain gray label, no icon, gray rank numbers - it's a "room to improve" callout,
             not a second medal podium; an AlertTriangle/red framing here would read as punitive. An
             empty Worst list (Best consumed the whole qualifying pool - normal in a small Directory)
             renders a note instead of nothing. -->
        <template #body>
            <div class="w-full">
                <div class="border-b border-gray-100 px-6 pb-4 pt-6">
                    <div class="flex items-center gap-2 pr-8 text-lg font-semibold text-gray-900">
                        <Medal class="h-5 w-5 text-amber-600" aria-hidden="true" />
                        {{ $t('views.objectives.texts.leaderboardTitle') }}
                    </div>
                    <!-- Which window these figures are based on - same reference
                         window (weeks) as the rest of the page, since the leaderboard is recomputed
                         from that same overview query, not a query of its own. -->
                    <p class="pl-7 pr-8 text-xs text-gray-400">{{ $tc('views.objectives.texts.leaderboardWindowSubtitle', windowWeeks) }}</p>
                </div>

                <div class="px-6 pt-4">
                    <p class="text-xs font-semibold uppercase tracking-wide text-amber-700">{{ $t('views.objectives.texts.bestPerformersLabel') }}</p>
                </div>
                <ol class="flex flex-col gap-3 px-6 py-3">
                    <li v-for="(entry, index) in best" :key="entry.barnId" class="flex items-center gap-3 text-sm">
                        <span class="w-5 shrink-0 text-right font-bold text-amber-700">{{ index + 1 }}</span>
                        <span class="min-w-0 flex-1 truncate font-medium text-gray-800" :title="entry.barnName">{{ entry.barnName }}</span>
                        <span class="max-w-[9rem] truncate text-xs text-gray-400" :title="entry.farmName">{{ entry.farmName }}</span>
                        <!-- Deviation colour, not a flat "green because it's ranked" - see LeaderboardEntry's
                             own note on resolvedTarget. -->
                        <span class="shrink-0 font-bold" :class="objectiveValueColourClass(entry)">{{ $n(entry.currentValue, 'decimal') }}%</span>
                    </li>
                </ol>

                <div class="border-t border-gray-100 px-6 pb-5 pt-4">
                    <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">{{ $t('views.objectives.texts.worstPerformersLabel') }}</p>
                    <p v-if="worst.length === 0" class="pt-3 text-xs italic text-gray-400">{{ $t('views.objectives.texts.worstPerformersEmptyNote') }}</p>
                    <ol v-else class="flex flex-col gap-3 pt-3">
                        <li v-for="(entry, index) in worst" :key="entry.barnId" class="flex items-center gap-3 text-sm">
                            <span class="w-5 shrink-0 text-right font-bold text-gray-400">{{ index + 1 }}</span>
                            <span class="min-w-0 flex-1 truncate font-medium text-gray-800" :title="entry.barnName">{{ entry.barnName }}</span>
                            <span class="max-w-[9rem] truncate text-xs text-gray-400" :title="entry.farmName">{{ entry.farmName }}</span>
                            <span class="shrink-0 font-bold" :class="objectiveValueColourClass(entry)">{{ $n(entry.currentValue, 'decimal') }}%</span>
                        </li>
                    </ol>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Modal } from '@makeabledk/vue-ui/components/modal';
import { Medal } from 'lucide-vue-next';
import CloseModalButton from '@/components/common/CloseModalButton.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import type { LeaderboardEntry } from './leaderboard';
import { objectiveValueColourClass } from './objectiveDisplay';

const props = defineProps<{
    best: LeaderboardEntry[];
    worst: LeaderboardEntry[];
    // Which of the two gating conditions is unmet, or null once both hold (see Index.vue's own note
    // on why this is disabled rather than v-if'd away, and on why it's counted in Barns not Farms).
    disabledReason: 'minBarns' | 'noData' | null;
    // Same reference window (weeks) driving the rest of the page: the modal's figures come from that
    // same overview query, so the subtitle needs to say which window they're based on rather than
    // leaving that implicit.
    windowWeeks: number;
}>();

const disabledTooltip = computed<string>(() => {
    if (props.disabledReason === 'minBarns') return i18n.global.t('views.objectives.texts.leaderboardMinBarnsTooltip');
    if (props.disabledReason === 'noData') return i18n.global.t('views.objectives.texts.leaderboardNoDataTooltip');
    return i18n.global.t('views.objectives.texts.leaderboardTitle');
});
</script>
